import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { apiRequest } from '@department-of-veterans-affairs/platform-utilities/api';
import { Title } from 'platform/forms-system/src/js/web-component-patterns';

import { URL, envApiUrl } from '../constants';
import { useSearchIdGuard } from '../hooks/useSearchIdGuard';
import { mapEducationResult } from '../utils/facility-search';
import {
  FacilitySearchResults,
  PER_PAGE,
  isRadioListShown,
} from './search/FacilitySearchResults';
import { SearchControls } from './search/SearchControls';

/**
 * @typedef {import('../types/education-facilities').EducationFacilitySearchResponse} EducationFacilitySearchResponse
 * @typedef {import('../types/facility-search').SearchStatus} SearchStatus
 */

export const SEARCH_SCHOOLS_PAGE_TITLE =
  'School or training facility information';

const EMPTY_QUERY_ERROR = 'Enter a facility name or code';

/** @type {EducationFacilitySearchResponse} Before the first search */
const EMPTY_RESULTS = {
  data: [],
  links: { self: '' },
  meta: { count: 0 },
};

/**
 * @param {string} query
 * @param {number} pageNum
 */
const buildSearchUrl = (query, pageNum) =>
  `${envApiUrl}${URL.GET_SCHOOL}search?name=${encodeURIComponent(
    query,
  )}&page=${pageNum}&per_page=${PER_PAGE}`;

/**
 * Page-level `'ui:field'` for `searchSchoolsPage`. It replaces the page body, so it
 * renders the page title itself.
 *
 * The schema's `required` check still runs on Continue. This field decides how that
 * shows: the required message on the radio list when it's shown, otherwise the
 * search box's own message.
 *
 * It renders the same in review mode: Ask VA's review page can leave this page in review
 * mode while its section is being edited, so hiding it there would leave nothing to edit.
 * @param {object} props
 * @param {object} [props.formData] The whole form's data
 * @param {(formData: object) => void} props.onChange
 * @param {object} [props.errorSchema]
 * @param {{ submitted?: boolean }} [props.formContext]
 */
export function EducationFacilitySearch({
  formData = {},
  onChange,
  errorSchema,
  formContext,
}) {
  const { school } = formData;
  const [showCurrentSelection] = useState(!!school); // Persist if a school is already selected
  const [rawResults, setRawResults] = useState(EMPTY_RESULTS);
  const [resultsQuery, setResultsQuery] = useState('');
  const [pageNum, setPageNum] = useState(1);
  const [status, setStatus] = useState(/** @type {SearchStatus} */ ('idle'));
  const [searchInvalid, setSearchInvalid] = useState(false);
  // SchemaForm replaces formContext with a new object on every Continue
  const [formContextAtLastSearch, setFormContextAtLastSearch] = useState(null);
  const { startSearch } = useSearchIdGuard();

  /**
   * The only entry point for searches (text, page change). Don't call
   * apiRequest directly. Results, query, and page are updated together, and only on success.
   * On failure the previous results stay visible and selectable.
   * Responses from searches that have since been replaced are ignored.
   * @param {string} query
   * @param {number} [newPageNum]
   */
  const runSearch = (query, newPageNum = 1) => {
    const { isLatest } = startSearch();

    setStatus('searching');
    setFormContextAtLastSearch(formContext);
    return apiRequest(buildSearchUrl(query, newPageNum))
      .then((/** @type {EducationFacilitySearchResponse} */ res) => {
        if (!isLatest()) return;
        setRawResults(res);
        setResultsQuery(query);
        setPageNum(newPageNum);
        setStatus('idle');
      })
      .catch(() => {
        if (isLatest()) setStatus('error');
      });
  };

  /** @param {string} query Trimmed, possibly empty */
  const handleSubmit = query => {
    setSearchInvalid(!query);
    if (query) runSearch(query);
  };

  /** @param {number} newPageNum */
  const handlePageChange = newPageNum => runSearch(resultsQuery, newPageNum);

  const isSearching = status === 'searching';
  const showRadio = isRadioListShown({
    resultCount: rawResults.data.length,
    resultsQuery,
    isSearching,
    showNotListed: true,
  });

  // A failed Continue's error shouldn't carry over to a later search
  const hasContinuedSinceLastSearch = formContext !== formContextAtLastSearch;
  // liveValidate sets the error on every render, so show it only after Continue
  const validationError =
    (formContext?.submitted &&
      hasContinuedSinceLastSearch &&
      errorSchema?.school?.__errors?.[0]) ||
    '';
  const radioError = showRadio ? validationError : '';
  const searchError =
    searchInvalid || (validationError && !showRadio) ? EMPTY_QUERY_ERROR : '';

  return (
    <>
      <Title title={SEARCH_SCHOOLS_PAGE_TITLE} />
      <div className="facility-locator vads-u-margin-top--2">
        <SearchControls
          onSubmit={handleSubmit}
          searchLabel="Search by facility name or code"
          error={searchError}
        />
        <div role="alert">
          {status === 'error' && (
            <va-alert status="error" class="vads-u-margin-y--2" uswds>
              <p className="vads-u-margin-y--0">
                We’re having trouble finding facilities right now. Try searching
                again or select “Finish this question later.”
              </p>
            </va-alert>
          )}
        </div>
        {showCurrentSelection && (
          <div id="facility-current-selection">
            <p>
              <strong>Your current selection:</strong>
            </p>
            <p>{school}</p>
          </div>
        )}
        <FacilitySearchResults
          results={rawResults.data.map(mapEducationResult)}
          pageNum={pageNum}
          totalCount={rawResults.meta.count}
          onPageChange={handlePageChange}
          // formData is the whole form, so keep the other keys
          onRadioSelect={value => onChange({ ...formData, school: value })}
          radioLabel="Select school facility"
          noResultsMessage={`We couldn’t find any results for ${resultsQuery}. Check the school facility’s name or code you entered or select “My facility is not listed.”`}
          selected={school}
          resultsQuery={resultsQuery}
          showNotListed
          error={radioError}
          isSearching={isSearching}
        />
      </div>
    </>
  );
}

EducationFacilitySearch.propTypes = {
  onChange: PropTypes.func.isRequired,
  errorSchema: PropTypes.object,
  formContext: PropTypes.shape({
    submitted: PropTypes.bool,
  }),
  formData: PropTypes.object,
};
